<template>
    <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <!-- Header -->
        <div class="mb-8">
            <h1 class="text-3xl font-bold text-gray-900 dark:text-white">Settings</h1>
            <p class="mt-2 text-gray-600 dark:text-gray-400">Manage your account and preferences</p>
        </div>

        <div class="flex gap-8">
            <!-- Left Navigation Panel -->
            <div class="w-64 flex-shrink-0">
                <nav class="space-y-1">
                    <button
                        v-for="section in sections"
                        :key="section.id"
                        @click="activeSection = section.id"
                        :class="[
                            'w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors',
                            activeSection === section.id
                                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-100'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800'
                        ]"
                    >
                        <component :is="section.icon" class="w-5 h-5 mr-3" />
                        {{ section.label }}
                    </button>
                </nav>
            </div>

            <!-- Main Content Area -->
            <div class="flex-1">
                <!-- Profile Section -->
                <div v-show="activeSection === 'profile'" class="card">
                    <div class="card-header">
                        <h2 class="text-lg font-medium">Profile</h2>
                    </div>
                    <div class="card-body space-y-6">
                        <!-- Avatar Upload -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Profile Picture
                            </label>
                            <div class="flex items-center space-x-4">
                                <div class="w-16 h-16 rounded-full bg-primary-600 flex items-center justify-center text-white text-xl font-medium">
                                    <img 
                                        v-if="authStore.user?.avatar_url" 
                                        :src="authStore.user.avatar_url" 
                                        :alt="authStore.user.username"
                                        class="w-16 h-16 rounded-full object-cover"
                                    />
                                    <span v-else>{{ userInitial }}</span>
                                </div>
                                <div class="space-x-2">
                                    <input
                                        ref="fileInput"
                                        type="file"
                                        accept="image/*"
                                        @change="handleAvatarUpload"
                                        class="hidden"
                                    />
                                    <button
                                        @click="fileInput?.click()"
                                        :disabled="authStore.isLoading"
                                        class="btn-secondary"
                                    >
                                        Upload New
                                    </button>
                                    <button
                                        v-if="authStore.user?.avatar_url"
                                        @click="handleAvatarRemove"
                                        :disabled="authStore.isLoading"
                                        class="btn-secondary"
                                    >
                                        Remove
                                    </button>
                                </div>
                            </div>
                            <p class="mt-2 text-sm text-gray-500">JPG, PNG, GIF up to 2MB</p>
                        </div>

                        <!-- Username (read-only) -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Username
                            </label>
                            <input
                                type="text"
                                :value="authStore.user?.username"
                                disabled
                                class="input bg-gray-50 dark:bg-gray-700"
                            />
                        </div>
                    </div>
                </div>

                <!-- Security Section -->
                <div v-show="activeSection === 'security'" class="card">
                    <div class="card-header">
                        <h2 class="text-lg font-medium">Security</h2>
                    </div>
                    <div class="card-body">
                        <form @submit.prevent="handlePasswordChange" class="space-y-6">
                            <div v-if="passwordError" class="p-3 bg-error-50 border border-error-200 rounded-md">
                                <p class="text-sm text-error-600">{{ passwordError }}</p>
                            </div>
                            
                            <div v-if="passwordSuccess" class="p-3 bg-success-50 border border-success-200 rounded-md">
                                <p class="text-sm text-success-600">Password updated successfully!</p>
                            </div>

                            <div>
                                <label for="current-password" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Current Password
                                </label>
                                <input
                                    id="current-password"
                                    v-model="currentPassword"
                                    type="password"
                                    required
                                    class="input"
                                    :disabled="authStore.isLoading"
                                />
                            </div>

                            <div>
                                <label for="new-password" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    New Password
                                </label>
                                <input
                                    id="new-password"
                                    v-model="newPassword"
                                    type="password"
                                    required
                                    minlength="8"
                                    class="input"
                                    :disabled="authStore.isLoading"
                                />
                                <p class="mt-1 text-sm text-gray-500">Must be at least 8 characters long</p>
                            </div>

                            <div>
                                <label for="confirm-password" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Confirm New Password
                                </label>
                                <input
                                    id="confirm-password"
                                    v-model="confirmPassword"
                                    type="password"
                                    required
                                    class="input"
                                    :disabled="authStore.isLoading"
                                />
                            </div>

                            <div>
                                <button
                                    type="submit"
                                    :disabled="authStore.isLoading || !canSubmitPassword"
                                    class="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <span v-if="authStore.isLoading">Updating...</span>
                                    <span v-else>Update Password</span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                <!-- Appearance Section -->
                <div v-show="activeSection === 'appearance'" class="card">
                    <div class="card-header">
                        <h2 class="text-lg font-medium">Appearance</h2>
                    </div>
                    <div class="card-body">
                        <div>
                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Theme
                            </label>
                            <select v-model="theme" @change="handleThemeChange" class="input">
                                <option value="system">System Default</option>
                                <option value="light">Light</option>
                                <option value="dark">Dark</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, h } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useTheme } from '@/composables/useTheme'

const authStore = useAuthStore()
const { theme, setTheme } = useTheme()

// Navigation state
const activeSection = ref('profile')

// Define icons as simple SVG components
const UserIcon = () => h('svg', {
    fill: 'none',
    stroke: 'currentColor',
    viewBox: '0 0 24 24',
    xmlns: 'http://www.w3.org/2000/svg'
}, [
    h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'
    })
])

const ShieldIcon = () => h('svg', {
    fill: 'none',
    stroke: 'currentColor',
    viewBox: '0 0 24 24',
    xmlns: 'http://www.w3.org/2000/svg'
}, [
    h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z'
    })
])

const PaletteIcon = () => h('svg', {
    fill: 'none',
    stroke: 'currentColor',
    viewBox: '0 0 24 24',
    xmlns: 'http://www.w3.org/2000/svg'
}, [
    h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z'
    })
])

// Navigation sections
const sections = [
    { id: 'profile', label: 'Profile', icon: UserIcon },
    { id: 'security', label: 'Security', icon: ShieldIcon },
    { id: 'appearance', label: 'Appearance', icon: PaletteIcon }
]

// Avatar management
const fileInput = ref<HTMLInputElement>()

// Password change
const currentPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')
const passwordError = ref<string | null>(null)
const passwordSuccess = ref(false)


const userInitial = computed(() => {
    return authStore.user?.username?.charAt(0).toUpperCase() || '?'
})

const canSubmitPassword = computed(() => {
    return currentPassword.value.length > 0 &&
           newPassword.value.length >= 8 &&
           newPassword.value === confirmPassword.value
})

const handleAvatarUpload = async (event: Event) => {
    const file = (event.target as HTMLInputElement).files?.[0]
    if (!file) return

    // Check file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
        alert('File size must be less than 2MB')
        return
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
        alert('Please select an image file')
        return
    }

    const success = await authStore.uploadAvatar(file)
    if (!success && authStore.error) {
        alert(authStore.error)
    }
}

const handleAvatarRemove = async () => {
    if (confirm('Are you sure you want to remove your profile picture?')) {
        const success = await authStore.removeAvatar()
        if (!success && authStore.error) {
            alert(authStore.error)
        }
    }
}

const handlePasswordChange = async () => {
    passwordError.value = null
    passwordSuccess.value = false

    if (newPassword.value !== confirmPassword.value) {
        passwordError.value = 'New passwords do not match'
        return
    }

    const success = await authStore.changePassword(currentPassword.value, newPassword.value)
    
    if (success) {
        passwordSuccess.value = true
        currentPassword.value = ''
        newPassword.value = ''
        confirmPassword.value = ''
        
        // Hide success message after 3 seconds
        setTimeout(() => {
            passwordSuccess.value = false
        }, 3000)
    } else {
        passwordError.value = authStore.error || 'Failed to update password'
    }
}

const handleThemeChange = () => {
    setTheme(theme.value)
}

</script>